import React, {FC, useCallback, useEffect, useState} from 'react';
import {loginStepSlice} from '../../../../../redux/reducers/runtime/loginStep';
import {LoginState} from '../../../../../shared/models/login/loginStep';
import {useMap} from './useMap';
import {DeliveryType} from '../../../../../types/registerCardApplicationRequestPayload';
import {useAppDispatch, useAppSelector} from '../../../../../redux';
import {cardApplicationIsExpired, esiaRegistrationInfoSelector} from '../../../../../redux/selectors/login';
import {loginSlice} from '../../../../../redux/reducers/login';
import {addNotification} from '../../../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../../../shared/components/snackbar/model';
import {getHttpErrorMessage} from '../../../../../shared/utils/getHttpErrorMessage';
import {GetIssuePointCabinetResponsePayload} from '../../../../../shared/models/api/getIssuePointCabinetResponsePayload';
import {
  EsiaRegCardMapProps,
  initialYandexZoomValue,
  registerParentStoreActivationTokenReturnID,
  saveCreatedApplicationSnapshot,
  setCardDeliveryInfo,
  submitKidsDataAndSaveErrors,
} from './models';
import {mapEsiaRegInfoToFormValues, mapFormValuesToEsiaRegData} from '../formMapper';
import {Screens} from '../../../../../shared/routing';
import {RegExps} from '../../../../../shared/constants/regexps';
import {getFormValues} from 'redux-form';
import {useRegCardState} from '../../esiaRegCardState/useRegCardState';
import {Forms} from '../../../../../shared/models/forms';
import {EsiaRegCardForm} from '../form/model';
import {holderProfileSelector} from '../../../../../redux/selectors/profile';
import {blockReissueIfAlreadySubmitted} from '../../../../../redux/actions/cardReissue';
import {CardStatus} from '../../../../../types/enums/cardStatus';
import {ShowResultModal} from '../modal/ShowResultModal';
import {useModal} from '../../../../../shared/components/modal';
import {EsiaRegCardMapWebLayout} from './web/EsiaRegCardMapWebLayout';
import {useIsDesktop} from '../../../../../shared/hooks/useIsDesktop';
import {CardApplicationStatus} from '../../../../../types/enums/cardApplicationStatus';

const latOffset = 0.007;

export const EsiaRegCardMap: FC<EsiaRegCardMapProps> = ({onGoBack, navigation}) => {
  const dispatch = useAppDispatch();
  const [submitting, setSubmitting] = useState(false);
  const {loading, mapData, onPressMarker, currentMarker, centerPoint, markers} = useMap();
  const regInfo = useAppSelector(esiaRegistrationInfoSelector);
  const [russianPostDeliveryAddress, setRussianPostDeliveryAddress] = useState<string>('');
  const [isDeliveryPointSelected, setIsDeliveryPointSelected] = useState(false);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState(DeliveryType.IssuePoint);
  const [isRussianPostDeliveryAddressValid, setIsRussianPostDeliveryAddressValid] = useState(true);
  const [mapZoomLevel, setMapZoomLevel] = React.useState(initialYandexZoomValue);
  const [mapCenterPoint, setMapCenterPoint] = React.useState<[number, number]>(centerPoint);
  const [isIssuePointDetailsVisible, setIsIssuePointDetailsVisible] = useState(true);
  const {parent, confirmedKids} = useRegCardState();
  const profile = useAppSelector(holderProfileSelector());
  const isLogged = !!profile;
  const modals = useModal();
  const isDesktop = useIsDesktop();
  const esiaRegInfo = useAppSelector(esiaRegistrationInfoSelector);
  const applicationIsExpired = useAppSelector(cardApplicationIsExpired);
  const formValues = useAppSelector((state) => {
    if (applicationIsExpired && esiaRegInfo) {
      if (parent) parent.checked = true;
      return mapEsiaRegInfoToFormValues(esiaRegInfo);
    }
    return getFormValues(Forms.esiaRegCardForm)(state) as EsiaRegCardForm;
  });
  const showModalProps = {
    modals,
    dispatch,
    isLogged,
    isDesktop,
    navigation,
    formValues,
  };

  useEffect(() => {
    if (selectedDeliveryType === DeliveryType.IssuePoint) setIsDeliveryPointSelected(currentMarker != null);
    else {
      setIsRussianPostDeliveryAddressValid(
        !russianPostDeliveryAddress.trim() || RegExps.regionRestriction.test(russianPostDeliveryAddress),
      );
      setIsDeliveryPointSelected(!!russianPostDeliveryAddress);
    }
  }, [russianPostDeliveryAddress, currentMarker, selectedDeliveryType]);

  const onDeliveryTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDeliveryType(Number.parseInt((event.target as HTMLInputElement).value, 10));
  };

  const onClientRegisterOrderPress = async () => {
    let parentAccountId: number | null = null;
    try {
      if (!regInfo) return;
      if (selectedDeliveryType === DeliveryType.IssuePoint && currentMarker?.id == null) return;
      setSubmitting(true);
      if (profile?.cardholderId) parentAccountId = profile.cardholderId;
      const isReissue =
        profile?.cardStatusId === CardStatus.Blocked ||
        parent?.cardStatusId === CardStatus.Blocked ||
        regInfo.cardStatusId === CardStatus.Blocked;
      if (isReissue && (await dispatch(blockReissueIfAlreadySubmitted()))) {
        return;
      }
      if (!formValues) {
        throw new Error('Нет данных для отправки заявления. Вернитесь к заполнению формы.');
      }
      const submissionData = mapFormValuesToEsiaRegData(formValues, regInfo.token);
      const cardDeliveryInfo = setCardDeliveryInfo(selectedDeliveryType, currentMarker, russianPostDeliveryAddress);
      await dispatch(
        loginSlice.actions.setEsiaRegistrationInfo({
          ...regInfo,
          cardDeliveryInfo,
          cardApplicationStatusId: CardApplicationStatus.AcceptFromClient,
        }),
      );
      const parentRegistration = await registerParentStoreActivationTokenReturnID(
        parent,
        submissionData,
        cardDeliveryInfo,
      );
      if (!parentRegistration.registered && confirmedKids.length === 0) {
        throw new Error('Нет данных для отправки заявления. Вернитесь к заполнению формы.');
      }
      parentAccountId = parentRegistration.counterpartyId ?? parentAccountId;
      const errorsOnKidsRegistration = submitKidsDataAndSaveErrors(
        confirmedKids,
        formValues,
        regInfo,
        parentAccountId,
        cardDeliveryInfo,
      );
      if (errorsOnKidsRegistration.length > 0) {
        ShowResultModal({...showModalProps, message: errorsOnKidsRegistration});
        return;
      } else if (parent?.checked || errorsOnKidsRegistration.length === 0) {
        ShowResultModal(showModalProps);
      }

      let redirectToActivation = false;
      if (parentRegistration.registered) {
        ({redirectToActivation} = await saveCreatedApplicationSnapshot(
          dispatch,
          regInfo,
          cardDeliveryInfo,
          parent,
          isLogged,
        ));
      }
      ShowResultModal({...showModalProps, redirectToActivation});
    } catch (e) {
      const errorText = getHttpErrorMessage(e, 'Ошибка. Повторите еще раз');
      dispatch(
        addNotification({
          message: errorText,
          type: SnackbarNotificationType.Error,
        }),
      );
      ShowResultModal({...showModalProps, message: errorText});
    } finally {
      setSubmitting(false);
    }
  };

  const onIssuePointSelected = (issuePoint: GetIssuePointCabinetResponsePayload | null) => {
    if (issuePoint == null) return;
    onPressMarker(issuePoint);
    setIsIssuePointDetailsVisible(true);
    if (issuePoint != null && issuePoint.geolocation && issuePoint.geolocation.length === 2) {
      setMapZoomLevel(14);
      setMapCenterPoint([issuePoint.geolocation[0] + latOffset, issuePoint.geolocation[1]]);
    }
  };

  useEffect(() => {
    if (currentMarker == null && mapData.length) {
      setMapZoomLevel(9);
      setMapCenterPoint(centerPoint);
    }
  }, [mapData]);

  const goBack = useCallback(() => {
    if (!navigation || isDesktop) return;
    if (navigation.canGoBack() && !applicationIsExpired) {
      navigation.goBack();
    } else {
      if (applicationIsExpired) {
        dispatch(loginStepSlice.actions.setLoginStep(LoginState.enter));
        dispatch(loginSlice.actions.resetEsiaFlow(null));
      }
      navigation.navigate(Screens.Login);
    }
  }, [navigation]);

  return (
    <EsiaRegCardMapWebLayout
      isMapDataLoading={loading}
      selectedDeliveryType={selectedDeliveryType}
      onDeliveryTypeChange={onDeliveryTypeChange}
      mapData={mapData}
      onIssuePointSelected={onIssuePointSelected}
      currentMarker={currentMarker}
      mapCenterPoint={mapCenterPoint}
      mapZoomLevel={mapZoomLevel}
      isIssuePointDetailsVisible={isIssuePointDetailsVisible}
      setIsIssuePointDetailsVisible={setIsIssuePointDetailsVisible}
      markers={markers}
      russianPostDeliveryAddress={russianPostDeliveryAddress}
      isRussianPostDeliveryAddressValid={isRussianPostDeliveryAddressValid}
      setRussianPostDeliveryAddress={setRussianPostDeliveryAddress}
      onClientRegisterOrderPress={onClientRegisterOrderPress}
      submitting={submitting}
      isDeliveryPointSelected={isDeliveryPointSelected}
      isLogged={isLogged}
      goBack={goBack}
      onGoBack={onGoBack}
    />
  );
};
